import type { Cell, CellWithRowAndCol } from "@fortune-sheet/core";
import ExcelJS from "exceljs";
import { extractPageSetup, type SheetWithPageSetup } from "./excelPageSetup";

/**
 * The spreadsheet PREVIEW's own default column width, and what "reset column widths" resets a column to.
 * Deliberately NOT Excel's default (see `EXCEL_DEFAULT_COLUMN_WIDTH_PX`): this is a UI affordance, and the
 * week-staff export writes it into its workbook so an undragged column measures the same in both.
 */
export const DEFAULT_COLUMN_WIDTH = 120;

/** Excel's own width for a column nobody sized: 8.43 characters, i.e. 64px. A column with no explicit
 *  `width` really is this wide when Excel prints it — this is not a fallback for missing data. */
export const EXCEL_DEFAULT_COLUMN_WIDTH_CHARS = 8.43;

/**
 * Excel's default body font size, and the height of a row sized for exactly one line of it. The ratio
 * between them IS Excel's rule for how much row a line of text needs, which both `excelWrapText` and
 * `downloadDayViewPdf` apply; they live together so the two modules cannot drift apart on it.
 */
export const EXCEL_DEFAULT_FONT_SIZE_PT = 11;
export const EXCEL_DEFAULT_ROW_HEIGHT_PT = 15;

/**
 * Excel's column-width unit is "how many digits of the default font fit in the column", and the pixel
 * width it renders is `chars * MDW + 5` — MDW being that font's widest digit, 7px for the Calibri 11 every
 * workbook here uses, plus a fixed 5px of cell padding. The padding is why a single multiplier cannot
 * express this: a plain `chars * 8` is off by a different amount at every column width.
 */
const MAX_DIGIT_WIDTH_PX = 7;
const COLUMN_PADDING_PX = 5;

/** Excel treats a stored width of 0 as a hidden column, which occupies no pixels — no padding is added. */
const HIDDEN_COLUMN_WIDTH = 0;

/** Excel character units to the pixels Fortune Sheet lays a column out in. Exported so a caller that must
 *  predict an on-screen width before conversion derives the same number rather than copying the formula. */
export const charsToPx = (chars: number): number =>
	chars <= HIDDEN_COLUMN_WIDTH ? HIDDEN_COLUMN_WIDTH : Math.round(chars * MAX_DIGIT_WIDTH_PX + COLUMN_PADDING_PX);

/** `EXCEL_DEFAULT_COLUMN_WIDTH_CHARS` in the pixels a column is laid out in — 64px. */
export const EXCEL_DEFAULT_COLUMN_WIDTH_PX = charsToPx(EXCEL_DEFAULT_COLUMN_WIDTH_CHARS);

/** The exact inverse of `charsToPx`, for writing a width dragged in the pixel-based preview back into the
 *  workbook. Deliberately unrounded: Excel stores fractional widths, and rounding drifts on round trips. */
export const pxToChars = (px: number): number => (px <= COLUMN_PADDING_PX ? HIDDEN_COLUMN_WIDTH : (px - COLUMN_PADDING_PX) / MAX_DIGIT_WIDTH_PX);

/** Fortune Sheet's text-break mode for "wrap inside the cell". */
const TEXT_BREAK_WRAP = "2";

const CONTAINS_LINE_BREAK = /\r\n|\r|\n/;

/** Excel row heights are in points; Fortune Sheet's `rowlen` is in pixels — copying the number across
 *  unconverted under-sizes every row by a quarter, which clips wrapped text. */
const POINTS_TO_PIXELS = 4 / 3;

/**
 * A row height as the renderers read it BACK, once it has been through `rowlen`'s whole pixels.
 *
 * The trip is lossy in both directions — 26pt comes back as 26.25 and 34pt as 33.75 — so a caller
 * budgeting a printed page has to count the height the row will actually be given, not the one it
 * asked for. Eight rows of a quarter-point are most of a line of text, which is the difference between
 * a page that fits and a page that spills its last row onto a second sheet of paper.
 */
export const roundTripRowHeightPt = (heightPt: number): number => Math.round(heightPt * POINTS_TO_PIXELS) / POINTS_TO_PIXELS;

type ConvertOptions = {
	/**
	 * Carry each cell's ExcelJS `wrapText` through as Fortune Sheet's wrap mode. Opt-in rather than
	 * automatic: ExcelJS reports column-level `wrapText` on every cell of that column, so enabling it for
	 * a sheet that never sets row heights would wrap text into vertical space that isn't there.
	 */
	applyWrapText: boolean;
};

/** Converts an ExcelJS buffer to Fortune Sheet format. Each sheet carries its worksheet's print setup
 *  (`pageSetup`) so the PDF renderer prints the paper and margins the workbook itself declares. */
export const excelBufferToFortuneSheet = async (
	buffer: ArrayBuffer,
	columnWidthOverrides?: Record<string, number>,
	options?: ConvertOptions,
): Promise<SheetWithPageSetup[]> => {
	const workbook = new ExcelJS.Workbook();
	await workbook.xlsx.load(buffer);

	const sheets: SheetWithPageSetup[] = [];

	workbook.eachSheet((worksheet, sheetId) => {
		const celldata: CellWithRowAndCol[] = [];
		const merges: string[] = worksheet.model.merges ?? [];

		// Build a set of cells that are part of merges (excluding master cells)
		// and a map of master cells to their merge info
		const mergedCells = new Set<string>();
		const masterCells = new Map<string, { r: number; c: number; rs: number; cs: number }>();

		merges.forEach((merge) => {
			const [start, end] = merge.split(":");
			const startCol = columnLetterToNumber(start.replace(/[0-9]/g, ""));
			const startRow = parseInt(start.replace(/[A-Z]/gi, ""), 10) - 1;
			const endCol = columnLetterToNumber(end.replace(/[0-9]/g, ""));
			const endRow = parseInt(end.replace(/[A-Z]/gi, ""), 10) - 1;

			const rs = endRow - startRow + 1;
			const cs = endCol - startCol + 1;

			// Store master cell merge info
			masterCells.set(`${startRow}_${startCol}`, { r: startRow, c: startCol, rs, cs });

			// Add all cells in the merge range except the master cell (top-left)
			for (let r = startRow; r <= endRow; r++) {
				for (let c = startCol; c <= endCol; c++) {
					if (r !== startRow || c !== startCol) {
						mergedCells.add(`${r}_${c}`);
					}
				}
			}
		});

		// `undefined` and `0` mean opposite things here — unsized, so Excel prints its default width, versus
		// HIDDEN and taking no page at all — so a truthiness test resurrects every hidden column at 64px.
		const columnWidths: number[] = [];
		worksheet.columns.forEach((col, index) => {
			columnWidths[index] = col.width === undefined ? EXCEL_DEFAULT_COLUMN_WIDTH_PX : charsToPx(col.width);
		});

		// Get row heights
		const rowHeights: Record<number, number> = {};

		worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
			const r = rowNumber - 1; // Fortune Sheet uses 0-based indexing

			if (row.height) {
				rowHeights[r] = Math.round(row.height * POINTS_TO_PIXELS);
			}

			row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
				const c = colNumber - 1; // Fortune Sheet uses 0-based indexing

				// Skip cells that are part of a merge (not the master cell)
				if (mergedCells.has(`${r}_${c}`)) {
					return;
				}

				const cellValue: Cell = {
					v: getCellValue(cell),
					m: cell.text || String(getCellValue(cell) ?? ""),
					ff: "Calibri", // Default font family
				};

				// Handle cell styling
				if (cell.style) {
					const style = cell.style;

					// Font styling
					if (style.font) {
						if (style.font.bold) cellValue.bl = 1;
						if (style.font.italic) cellValue.it = 1;
						// Underline and strike were missing here while nothing round-tripped a workbook that
						// used them; the printout's formatting overlay does, and without these two a cell the
						// user underlined came back plain and the PDF drew no rule. `underline` is `true` or a
						// style name ("double"), both of which mean underlined — Fortune Sheet has only the one.
						if (style.font.underline) cellValue.un = 1;
						if (style.font.strike) cellValue.cl = 1;
						if (style.font.size) cellValue.fs = style.font.size;
						if (style.font.name) cellValue.ff = style.font.name;
						if (style.font.color?.argb) {
							cellValue.fc = argbToHex(style.font.color.argb);
						}
					}

					// Background color
					if (style.fill && "fgColor" in style.fill && style.fill.fgColor?.argb) {
						cellValue.bg = argbToHex(style.fill.fgColor.argb);
					}

					// Alignment
					if (style.alignment) {
						if (style.alignment.horizontal) {
							cellValue.ht = alignmentToNumber(style.alignment.horizontal);
						}
						if (style.alignment.vertical) {
							cellValue.vt = verticalAlignmentToNumber(style.alignment.vertical);
						}
						if (options?.applyWrapText && style.alignment.wrapText) {
							cellValue.tb = TEXT_BREAK_WRAP;
						}
					}
				}

				// Add merge cell info if this is a master cell
				const mergeInfo = masterCells.get(`${r}_${c}`);
				if (mergeInfo) {
					cellValue.mc = mergeInfo;
				}

				if (options?.applyWrapText) {
					applyExplicitLineBreaks(cellValue);
				}

				const fortuneCell: CellWithRowAndCol = {
					r,
					c,
					v: cellValue,
				};

				celldata.push(fortuneCell);
			});
		});

		const sheet: SheetWithPageSetup = {
			name: worksheet.name,
			id: String(sheetId),
			pageSetup: extractPageSetup(worksheet),
			celldata,
			row: worksheet.rowCount || 50,
			column: worksheet.columnCount || 20,
			config: {
				columnlen: columnWidths.reduce(
					(acc, width, index) => {
						acc[index] = columnWidthOverrides?.[String(index)] ?? width;
						return acc;
					},
					{} as Record<number, number>,
				),
				rowlen: rowHeights,
				merge: mergesToConfig(merges),
			},
		};

		sheets.push(sheet);
	});

	return sheets;
};

/**
 * Make a cell's embedded newlines render as actual line breaks. Fortune Sheet does not break a plain string
 * on `\n` — its wrap mode only reflows text past the column edge — and honours explicit breaks only for an
 * *inline string* cell, so a multi-line value is re-expressed as a single styled inline run.
 *
 * `v` and `m` are deliberately left alone: the PDF renderer reads those and already handles newlines.
 */
const applyExplicitLineBreaks = (cellValue: Cell): void => {
	const text = cellValue.m;
	if (typeof text !== "string" || !CONTAINS_LINE_BREAK.test(text)) return;

	// Inline runs carry their own styling — cell-level font properties are ignored for them, so every
	// font key the converter reads has to be repeated here or it is invisible on a multi-line cell.
	// `un`/`cl` included: the PDF reads them off the CELL and so kept them, which is what made their
	// absence here a preview-only mismatch rather than a lost decoration.
	cellValue.ct = {
		t: "inlineStr",
		s: [{ v: text, fc: cellValue.fc, bl: cellValue.bl, it: cellValue.it, un: cellValue.un, cl: cellValue.cl, fs: cellValue.fs, ff: cellValue.ff }],
	};
};

const getCellValue = (cell: ExcelJS.Cell): string | number | boolean | undefined => {
	const value = cell.value;

	if (value === null || value === undefined) return undefined;

	if (typeof value === "object") {
		if ("result" in value) return value.result as string | number;
		if ("text" in value) return value.text as string;
		if ("richText" in value) {
			return (value.richText as { text: string }[]).map((rt) => rt.text).join("");
		}
		return String(value);
	}

	return value as string | number | boolean;
};

/** ExcelJS colours are `AARRGGBB`; Fortune Sheet's are CSS `#RRGGBB`. The alpha byte is the whole
 *  difference between the two, and `FF` is the only value any workbook here writes. */
const ARGB_LENGTH = 8;
const RGB_LENGTH = 6;
const OPAQUE_ALPHA_BYTE = "FF";

export const argbToHex = (argb: string): string => {
	if (argb.length === ARGB_LENGTH) {
		return `#${argb.substring(ARGB_LENGTH - RGB_LENGTH)}`;
	}
	return `#${argb}`;
};

/**
 * The inverse of `argbToHex` — deliberately its immediate neighbour, because nothing but adjacency stops
 * a pair like this drifting apart. Puts back the leading alpha byte its partner strips: ExcelJS writes
 * `FFRRGGBB`, and a six-digit `RRGGBB` is read as `00RRGGBB`, i.e. fully transparent.
 */
export const hexToArgb = (hex: string): string => {
	const value = hex.replace("#", "").toUpperCase();
	if (value.length === ARGB_LENGTH) return value;
	if (value.length !== RGB_LENGTH) throw new Error(`Cannot convert "${hex}" to an ARGB colour`);
	return `${OPAQUE_ALPHA_BYTE}${value}`;
};

export const alignmentToNumber = (alignment: string): number => {
	switch (alignment) {
		case "left":
			return 1;
		case "center":
			return 0;
		case "right":
			return 2;
		default:
			return 1;
	}
};

/** The inverse of `alignmentToNumber`, kept beside it for the reason `hexToArgb` is. Throws rather than
 *  guessing: a silently wrong alignment written back into a workbook is invisible in review. */
export const numberToAlignment = (ht: number): "left" | "center" | "right" => {
	switch (ht) {
		case 0:
			return "center";
		case 1:
			return "left";
		case 2:
			return "right";
		default:
			throw new Error(`Unknown Fortune-Sheet horizontal alignment code: ${ht}`);
	}
};

export const verticalAlignmentToNumber = (alignment: string): number => {
	switch (alignment) {
		case "top":
			return 1;
		case "middle":
			return 0;
		case "bottom":
			return 2;
		default:
			return 0;
	}
};

/** The inverse of `verticalAlignmentToNumber`, kept beside it. Throws on an unknown code for the same
 *  reason `downloadDayViewPdf`'s `verticalAlignFor` does. */
export const numberToVerticalAlignment = (vt: number): "top" | "middle" | "bottom" => {
	switch (vt) {
		case 0:
			return "middle";
		case 1:
			return "top";
		case 2:
			return "bottom";
		default:
			throw new Error(`Unknown Fortune-Sheet vertical alignment code: ${vt}`);
	}
};

const mergesToConfig = (merges: string[]): Record<string, { r: number; c: number; rs: number; cs: number }> => {
	const config: Record<string, { r: number; c: number; rs: number; cs: number }> = {};

	merges.forEach((merge) => {
		const [start, end] = merge.split(":");
		const startCol = columnLetterToNumber(start.replace(/[0-9]/g, ""));
		const startRow = parseInt(start.replace(/[A-Z]/gi, ""), 10) - 1;
		const endCol = columnLetterToNumber(end.replace(/[0-9]/g, ""));
		const endRow = parseInt(end.replace(/[A-Z]/gi, ""), 10) - 1;

		config[`${startRow}_${startCol}`] = {
			r: startRow,
			c: startCol,
			rs: endRow - startRow + 1,
			cs: endCol - startCol + 1,
		};
	});

	return config;
};

const columnLetterToNumber = (letters: string): number => {
	let result = 0;
	for (let i = 0; i < letters.length; i++) {
		result = result * 26 + letters.charCodeAt(i) - 64;
	}
	return result - 1; // 0-based index
};
